import {
  GetObservedDataOptions,
  GetObservedDataOptionsResponse,
  MeteorologyViewMode,
  ObservedDataPeriodMode,
  ObservedDataPredefinedPeriod,
  ObservedDataVariable,
  ObservedDataVisualization,
  RawGetObservedDataOptions,
  RawObservedDataPeriodMode,
  RawObservedDataVariable,
  RawObservedDataVisualization,
  RawObservedDataVisualizationId,
} from 'types/Meteorology/Fields/ObservedData/getOptions';

const transformPeriodMode = (rawMode: RawObservedDataPeriodMode): ObservedDataPeriodMode => {
  switch (rawMode) {
    case 'predefinido':
      return 'preset';
    case 'data':
      return 'date';
    case 'livre':
      return 'free';
    default:
      return rawMode as ObservedDataPeriodMode;
  }
};

const transformVisualizationId = (rawId: RawObservedDataVisualizationId): MeteorologyViewMode => (
  rawId === 'espacial' ? 'spatial' : 'heatmap'
);

const mapPeriodList = (
  periods: ObservedDataPredefinedPeriod[] | undefined,
): ObservedDataPredefinedPeriod[] => (
  (periods || []).map((option) => ({ id: option.id, label: option.label }))
);

const transformVariable = (rawVariable: RawObservedDataVariable): ObservedDataVariable => {
  const spatialFromMatrix = mapPeriodList(
    rawVariable.predefinido_por_visualizacao?.espacial,
  );
  const heatmapFromMatrix = mapPeriodList(
    rawVariable.predefinido_por_visualizacao?.heatmap,
  );
  const legacyPredefined = mapPeriodList(rawVariable.predefinido);

  // Fallback de retrocompat: se o BE ainda nao envia predefinido_por_visualizacao,
  // usamos `predefinido` (lista legacy) como periodos espaciais.
  const spatialPeriods = spatialFromMatrix.length > 0 ? spatialFromMatrix : legacyPredefined;

  // visualizacoes_suportadas vem do BE; quando ausente, derivamos pela presenca
  // de itens em cada lista (heatmap so se houver periodos correspondentes).
  const supportedFromBackend = (rawVariable.visualizacoes_suportadas || []).map(
    transformVisualizationId,
  );
  const supportedVisualizations: MeteorologyViewMode[] = supportedFromBackend.length > 0
    ? supportedFromBackend
    : [
      ...(spatialPeriods.length > 0 ? (['spatial'] as MeteorologyViewMode[]) : []),
      ...(heatmapFromMatrix.length > 0 ? (['heatmap'] as MeteorologyViewMode[]) : []),
    ];

  return {
    id: rawVariable.id,
    label: rawVariable.label,
    activeTabs: rawVariable.abas_ativas.map(transformPeriodMode),
    predefinedPeriods: legacyPredefined.length > 0 ? legacyPredefined : spatialPeriods,
    predefinedPeriodsByVisualization: {
      spatial: spatialPeriods,
      heatmap: heatmapFromMatrix,
    },
    supportedVisualizations,
  };
};

const transformVisualization = (
  raw: RawObservedDataVisualization,
): ObservedDataVisualization => ({
  id: transformVisualizationId(raw.id),
  label: raw.label,
  default: raw.default,
});

const DEFAULT_VISUALIZATIONS: ObservedDataVisualization[] = [
  { id: 'spatial', label: 'Espacial', default: true },
  { id: 'heatmap', label: 'Heatmap' },
];

const pickDefaultVisualization = (
  visualizations: ObservedDataVisualization[],
): MeteorologyViewMode => (
  visualizations.find((v) => v.default)?.id ?? 'spatial'
);

/* eslint-disable import/prefer-default-export */
export const transformGetObservedDataOptionsResponse = (
  response: GetObservedDataOptionsResponse,
): GetObservedDataOptions => {
  const raw = response.data as RawGetObservedDataOptions;
  const visualizations = raw.visualizacoes && raw.visualizacoes.length > 0
    ? raw.visualizacoes.map(transformVisualization)
    : DEFAULT_VISUALIZATIONS;

  return {
    regions: raw.regioes.map((region) => ({ id: region.id, label: region.label })),
    variables: raw.variables.map(transformVariable),
    visualizations,
    defaultVisualization: pickDefaultVisualization(visualizations),
  };
};
